import { Button, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackButton from '../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  ActivityHour,
  getStudyPlanByIdRequest,
  StudyPlanListItem,
  updateCourseRequest,
} from '../api';
import { CourseFormLoader } from '../hooks/CourseFormLoader';
import { CourseForm, CourseFormValues } from '../parts/Course/CourseForm';

export function UpdateCourse() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [studyPlan, setStudyPlan] = useState<StudyPlanListItem>();

  const goBack = useCallback(() => {
    history.push(`${PathsLayouts.continuousEducation}/study-plans`);
  }, [history]);

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );

  useEffect(() => {
    const fetchStudyPlan = async () => {
      const response = await getStudyPlanByIdRequest(Number(id));
      if (!response.error && response.data) {
        setStudyPlan(response.data);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.msg.createError`),
          className: 'long-toast',
        });
        goBack();
      }
    };

    fetchStudyPlan();
  }, [id, t, goBack]);

  const initialValues = useMemo(() => {
    if (!studyPlan) {
      return undefined;
    }

    // Extract activity hours from the latest version
    const activityHours: { [key: string]: number } = {};
    if (
      studyPlan.studyPlanCe &&
      studyPlan.studyPlanCe.activityTypeHours &&
      studyPlan.studyPlanCe.activityTypeHours.length > 0
    ) {
      studyPlan.studyPlanCe.activityTypeHours.forEach((ah) => {
        activityHours[ah.activityTypeId] = ah.hours || 0;
      });
    }

    const firstCourse =
      studyPlan.versions?.[0]?.curriculums?.[0]?.semesters?.[0]
        ?.semesterCourses?.[0]?.course;

    return {
      course: firstCourse
        ? {
            label: `${studyPlan.code} - ${studyPlan.name}`,
            value: firstCourse.id,
          }
        : null,
      name: studyPlan.name || '',
      code: studyPlan.code || '',
      modality: studyPlan.modality?.name || '',
      school: studyPlan.school?.name || '',
      totalHours: studyPlan.studyPlanCe?.hoursTotal || 0,
      activityHours,
      description: studyPlan.studyPlanCe?.description || '',
      hasCertificate: studyPlan.studyPlanCe?.hasCertificate || false,
    };
  }, [studyPlan]);

  const onSubmit = useCallback(
    async (values: CourseFormValues) => {
      if (!values.course) {
        return;
      }

      const activityHours: ActivityHour[] = Object.entries(
        values.activityHours,
      ).map(([activityTypeId, hours]) => ({
        activityTypeId: activityTypeId,
        hours: Number(hours || 0),
      }));

      const requestBody = {
        courseId: values.course.value as number,
        name: values.name,
        code: values.code,
        description: values.description,
        hasCertificate: values.hasCertificate,
        activityHours,
      };

      const { error } = await updateCourseRequest(Number(id), requestBody);
      if (!error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`common.msg.updateSuccess`),
          className: 'long-toast',
        });
        goBack();
        return;
      }

      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`common.msg.createError`),
        className: 'long-toast',
      });
      return error.code;
    },
    [t, goBack, id],
  );

  return (
    <Card className="p-4 mt-2 mx-3">
      <div className="mb-4">
        <GoBackButton onClick={goBack} />
      </div>
      <CourseFormLoader fetchImmediately>
        <CourseForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          isEditMode={true}
          isInActiveOffer={studyPlan?.isInActiveOffer}
        >
          {({ isSubmitting }) => (
            <Row className="pt-5">
              <Col
                xs={{ order: 2, size: 12 }}
                md={{ order: 1, size: 4 }}
                className="ml-auto"
              >
                <div>
                  <Button
                    type="button"
                    text={t(`common.actions.cancel`)}
                    outlined
                    onClick={() => goBack()}
                    fullwidth
                  />
                </div>
              </Col>
              {!studyPlan?.isInActiveOffer && (
                <Col xs={{ order: 1, size: 12 }} md={{ order: 2, size: 4 }}>
                  <div className="pb-3 pb-md-0">
                    <Button
                      type="submit"
                      text={t(`common.actions.update`)}
                      loading={isSubmitting}
                      fullwidth
                    />
                  </div>
                </Col>
              )}
            </Row>
          )}
        </CourseForm>
      </CourseFormLoader>
    </Card>
  );
}
